// @ts-strict-ignore
import { FetchResult, useApolloClient } from "@apollo/client";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@dashboard/config";
import {
  AddressInput,
  CustomerAddressesDocument,
  CustomerAddressesQuery,
  CustomerAddressesQueryVariables,
  OrderDetailsWithMetadataQueryResult,
  OrderDraftCancelMutation,
  OrderDraftCancelMutationVariables,
  OrderDraftFinalizeMutation,
  OrderDraftFinalizeMutationVariables,
  OrderDraftUpdateMutation,
  OrderDraftUpdateMutationVariables,
  OrderLineUpdateMutation,
  OrderLineUpdateMutationVariables,
  OrderNoteUpdateMutation,
  OrderNoteUpdateMutationVariables,
  SearchOrderCustomerEmailsDocument,
  SearchOrderCustomerEmailsQuery,
  SearchOrderCustomerEmailsQueryVariables,
  useChannelUsabilityDataQuery,
  useCreateCustomerMutation,
  useCustomerAddressesQuery,
  useUpdateCustomerMutation,
  useUpdateMetadataMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { CustomerEditData } from "@dashboard/orders/components/OrderCustomer";
import { OrderCustomerAddressesEditDialogOutput } from "@dashboard/orders/components/OrderCustomerAddressesEditDialog/types";
import {
  CustomerChangeActionEnum,
  OrderCustomerChangeData,
} from "@dashboard/orders/components/OrderCustomerChangeDialog/form";
import OrderCustomerChangeDialog from "@dashboard/orders/components/OrderCustomerChangeDialog/OrderCustomerChangeDialog";
import { OrderMetadataDialog } from "@dashboard/orders/components/OrderMetadataDialog";
import { getVariantSearchAddress, isAnyAddressEditModalOpen } from "@dashboard/orders/utils/data";
import { OrderDiscountProvider } from "@dashboard/products/components/OrderDiscountProviders/OrderDiscountProvider";
import { OrderLineDiscountProvider } from "@dashboard/products/components/OrderDiscountProviders/OrderLineDiscountProvider";
import useCustomerSearch from "@dashboard/searches/useCustomerSearch";
import { useOrderVariantSearch } from "@dashboard/searches/useOrderVariantSearch";
import { PartialMutationProviderOutput } from "@dashboard/types";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { useState } from "react";
import { useIntl } from "react-intl";

import { customerUrl } from "../../../../customers/urls";
import { extractMutationErrors, getStringOrPlaceholder } from "../../../../misc";
import { productUrl } from "../../../../products/urls";
import OrderAddressFields from "../../../components/OrderAddressFields/OrderAddressFields";
import OrderDraftCancelDialog from "../../../components/OrderDraftCancelDialog/OrderDraftCancelDialog";
import OrderDraftFinalizeDialog from "../../../components/OrderDraftFinalizeDialog/OrderDraftFinalizeDialog";
import OrderDraftPage from "../../../components/OrderDraftPage";
import OrderMarkAsPaidDialog from "../../../components/OrderMarkAsPaidDialog/OrderMarkAsPaidDialog";
import OrderProductAddDialog from "../../../components/OrderProductAddDialog";
import OrderShippingMethodEditDialog from "../../../components/OrderShippingMethodEditDialog";
import { orderDraftListUrl, OrderUrlDialog, OrderUrlQueryParams } from "../../../urls";
import { getOrderNif, ORDER_NIF_KEY } from "../../../utils/nif";

interface OrderDraftDetailsProps {
  id: string;
  params: OrderUrlQueryParams;
  loading: any;
  data: OrderDetailsWithMetadataQueryResult["data"];
  orderAddNote: any;
  orderUpdateNote: PartialMutationProviderOutput<
    OrderNoteUpdateMutation,
    OrderNoteUpdateMutationVariables
  >;
  orderLineUpdate: PartialMutationProviderOutput<
    OrderLineUpdateMutation,
    OrderLineUpdateMutationVariables
  >;
  orderLineDelete: any;
  orderShippingMethodUpdate: any;
  orderLinesAdd: any;
  orderPaymentMarkAsPaid: any;
  orderDraftUpdate: PartialMutationProviderOutput<
    OrderDraftUpdateMutation,
    OrderDraftUpdateMutationVariables
  >;
  orderDraftCancel: PartialMutationProviderOutput<
    OrderDraftCancelMutation,
    OrderDraftCancelMutationVariables
  >;
  orderDraftFinalize: PartialMutationProviderOutput<
    OrderDraftFinalizeMutation,
    OrderDraftFinalizeMutationVariables
  >;
  onBillingNifUpdate: (nif: string) => void;
  openModal: (action: OrderUrlDialog, newParams?: OrderUrlQueryParams) => void;
  closeModal: any;
}

export const OrderDraftDetails = ({
  id,
  params,
  loading,
  data,
  orderAddNote,
  orderUpdateNote,
  orderLineUpdate,
  orderLineDelete,
  orderShippingMethodUpdate,
  orderLinesAdd,
  orderPaymentMarkAsPaid,
  orderDraftUpdate,
  orderDraftCancel,
  orderDraftFinalize,
  onBillingNifUpdate,
  openModal,
  closeModal,
}: OrderDraftDetailsProps) => {
  const order = data.order;
  const navigate = useNavigator();
  const [finalizeNotifyCustomer, setFinalizeNotifyCustomer] = useState(true);
  const [transactionReference, setTransactionReference] = useState("");
  const [markAsPaidNotifyCustomer, setMarkAsPaidNotifyCustomer] = useState(true);
  const { data: channelUsabilityData } = useChannelUsabilityDataQuery({
    variables: {
      channel: order.channel.slug,
    },
  });
  const {
    loadMore,
    search: variantSearch,
    result: variantSearchOpts,
  } = useOrderVariantSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      channel: order.channel.slug,
      address: getVariantSearchAddress(order),
      isPublished: true,
    },
  });
  const {
    loadMore: loadMoreCustomers,
    search: searchUsers,
    result: users,
  } = useCustomerSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA,
  });
  // Emails of returning guest customers (orders but no account). Picking one
  // goes through the "new email" path, which creates the account; the backend
  // then fills it from the latest of those orders.
  const apolloClient = useApolloClient();
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const searchCustomers = async (query: string) => {
    searchUsers(query);

    const trimmed = query.trim();

    // Orders match on the complete email only (case-insensitive).
    if (!trimmed.includes("@")) {
      setGuestEmails([]);

      return;
    }

    const { data: ordersData } = await apolloClient.query<
      SearchOrderCustomerEmailsQuery,
      SearchOrderCustomerEmailsQueryVariables
    >({
      query: SearchOrderCustomerEmailsDocument,
      variables: { filter: { customer: trimmed } },
      fetchPolicy: "network-only",
    });
    const emails = mapEdgesToItems(ordersData?.orders)
      .filter(guestOrder => !guestOrder.user && guestOrder.userEmail)
      .map(guestOrder => guestOrder.userEmail.toLowerCase());

    setGuestEmails(Array.from(new Set(emails)));
  };
  const accountUsers = mapEdgesToItems(users?.data?.search) ?? [];
  const customerOptions = [
    ...accountUsers,
    ...guestEmails
      .filter(email => !accountUsers.some(user => user.email.toLowerCase() === email))
      .map(email => ({
        __typename: "User" as const,
        id: email,
        email,
        firstName: "",
        lastName: "",
      })),
  ];
  const { data: customerAddresses, loading: customerAddressesLoading } = useCustomerAddressesQuery({
    variables: {
      id: order?.user?.id,
    },
    skip: !order?.user?.id || !isAnyAddressEditModalOpen(params.action),
  });
  const intl = useIntl();
  const [createCustomer] = useCreateCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [updateMetadata] = useUpdateMetadataMutation();
  // Staff typing a brand-new email used to leave the draft with a guest
  // `userEmail` only, so the addresses and NIF entered next lived on that one
  // order and the customer had to be retyped on every new draft. Create the
  // customer account instead (no redirect URL, so no set-password email is
  // sent); if it can't be created (e.g. the email already exists), fall back
  // to the guest email, which the backend links to an existing active account.
  const createCustomerForEmail = async (email: string): Promise<string | null> => {
    const result = await createCustomer({
      variables: { input: { email, isActive: true } },
    });

    if (result.data?.customerCreate?.errors?.length) {
      return null;
    }

    return result.data?.customerCreate?.user?.id ?? null;
  };
  // Copy the customer's saved NIF onto the order when the order has none yet.
  const copyCustomerNif = async (userId: string) => {
    if (getOrderNif(order)) {
      return;
    }

    const { data: customerData } = await apolloClient.query<
      CustomerAddressesQuery,
      CustomerAddressesQueryVariables
    >({ query: CustomerAddressesDocument, variables: { id: userId }, fetchPolicy: "network-only" });
    const customerNif = customerData?.user?.metadata?.find(
      entry => entry.key === ORDER_NIF_KEY,
    )?.value;

    if (customerNif) {
      await onBillingNifUpdate(customerNif);
    }
  };
  const handleCustomerChange = async ({
    user,
    userEmail,
    prevUser,
    prevUserEmail,
  }: CustomerEditData) => {
    const sameUser = user && user === prevUser;
    // Re-entering the same email on a guest draft converts it to a customer.
    const sameUserEmail = userEmail && userEmail === prevUserEmail && !!order.user;

    if (sameUser || sameUserEmail) {
      return;
    }

    const createdUserId = userEmail && !user ? await createCustomerForEmail(userEmail) : null;
    const result = await orderDraftUpdate.mutate({
      id,
      input: createdUserId ? { user: createdUserId } : { user, userEmail },
    });

    if (result?.data?.draftOrderUpdate?.errors?.length) {
      return;
    }

    const linkedUserId = result?.data?.draftOrderUpdate?.order?.user?.id;

    // A newly created customer may already carry a NIF: the backend fills it
    // from the email's earlier guest orders.
    if (linkedUserId) {
      await copyCustomerNif(linkedUserId);
    }

    const modalUri = prevUser ? "customer-change" : "edit-customer-addresses";

    openModal(modalUri);
  };
  const handleCustomerChangeAction = (data: OrderCustomerChangeData) => {
    if (data.changeActionOption === CustomerChangeActionEnum.CHANGE_ADDRESS) {
      openModal("edit-customer-addresses");
    } else {
      closeModal();
    }
  };
  // Save the draft's addresses (and name) on the customer when the customer
  // doesn't have them yet, so the next draft for them comes prefilled.
  // Existing defaults are never overwritten from an order.
  const saveAddressesOnCustomer = async (
    user: OrderDraftUpdateMutation["draftOrderUpdate"]["order"]["user"],
    data: Partial<OrderCustomerAddressesEditDialogOutput>,
  ) => {
    const customer = customerAddresses?.user?.id === user.id ? customerAddresses.user : null;

    if (!customer) {
      return;
    }

    const nameSource: AddressInput | undefined = data.billingAddress ?? data.shippingAddress;
    const input = {
      ...(data.shippingAddress &&
        !customer.defaultShippingAddress && { defaultShippingAddress: data.shippingAddress }),
      ...(data.billingAddress &&
        !customer.defaultBillingAddress && { defaultBillingAddress: data.billingAddress }),
      ...(!customer.firstName && nameSource?.firstName && { firstName: nameSource.firstName }),
      ...(!customer.lastName && nameSource?.lastName && { lastName: nameSource.lastName }),
    };

    if (Object.keys(input).length) {
      await updateCustomer({ variables: { id: user.id, input } });
    }
  };
  const handleCustomerChangeAddresses = async (
    data: Partial<OrderCustomerAddressesEditDialogOutput>,
  ): Promise<FetchResult<OrderDraftUpdateMutation>> => {
    const result = await orderDraftUpdate.mutate({ id, input: data });
    const user = result?.data?.draftOrderUpdate?.order?.user;

    if (!result?.data?.draftOrderUpdate?.errors?.length && user) {
      await saveAddressesOnCustomer(user, data);
    }

    return result;
  };
  // The NIF lives in order metadata; on drafts keep the customer's copy in
  // sync too so it can be prefilled on their next order.
  const handleNifUpdate = async (nif: string) => {
    await onBillingNifUpdate(nif);

    const trimmedNif = nif.trim();

    if (order.user?.id && trimmedNif) {
      await updateMetadata({
        variables: {
          id: order.user.id,
          input: [{ key: ORDER_NIF_KEY, value: trimmedNif }],
          keysToDelete: [],
        },
      });
    }
  };
  // The customer note (mirrors the storefront checkout note as `customerNote`)
  // can only be set while the order is a draft, so it is editable here.
  const handleCustomerNoteUpdate = (customerNote: string) =>
    extractMutationErrors(orderDraftUpdate.mutate({ id, input: { customerNote } }));
  const handleDraftFinalize = async () => {
    const errors = await extractMutationErrors(
      orderDraftFinalize.mutate({ id, notifyCustomer: finalizeNotifyCustomer }),
    );

    if (!errors.length) {
      closeModal();
    }

    return errors;
  };
  const handleOrderDraftCancel = async () => {
    const errors = await extractMutationErrors(orderDraftCancel.mutate({ id }));

    if (!errors.length) {
      navigate(orderDraftListUrl());
    }

    return errors;
  };
  const errors = orderDraftFinalize.opts.data?.draftOrderComplete.errors || [];

  return (
    <>
      <WindowTitle
        title={intl.formatMessage(
          {
            id: "TLNf6K",
            defaultMessage: "Draft Order #{orderNumber}",
            description: "window title",
          },
          {
            orderNumber: getStringOrPlaceholder(data?.order?.number),
          },
        )}
      />

      <OrderDiscountProvider order={order}>
        <OrderLineDiscountProvider order={order}>
          <OrderDraftPage
            loading={loading}
            disabled={loading}
            errors={errors}
            onNoteUpdateLoading={orderUpdateNote.opts.loading}
            onNoteUpdate={(id, message) =>
              orderUpdateNote.mutate({
                order: id,
                input: {
                  message,
                },
              })
            }
            onNoteAdd={variables =>
              extractMutationErrors(
                orderAddNote.mutate({
                  input: variables,
                  order: id,
                }),
              )
            }
            onCustomerNoteUpdate={handleCustomerNoteUpdate}
            customerNoteUpdateButtonState={orderDraftUpdate.opts.status}
            users={customerOptions}
            hasMore={users?.data?.search?.pageInfo?.hasNextPage || false}
            onFetchMore={loadMoreCustomers}
            fetchUsers={searchCustomers}
            usersLoading={users.loading}
            onCustomerEdit={handleCustomerChange}
            onDraftFinalize={() => openModal("finalize")}
            onDraftRemove={() => openModal("cancel")}
            onOrderMarkAsPaid={() => openModal("mark-paid")}
            onOrderLineAdd={() => openModal("add-order-line")}
            onShowMetadata={id => openModal("view-metadata", { id })}
            order={order}
            channelUsabilityData={channelUsabilityData}
            onProductClick={id => () => navigate(productUrl(encodeURIComponent(id)))}
            onBillingAddressEdit={() => openModal("edit-billing-address")}
            onShippingAddressEdit={() => openModal("edit-shipping-address")}
            onShippingMethodEdit={() => openModal("edit-shipping")}
            onOrderLineRemove={id => orderLineDelete.mutate({ id })}
            onOrderLineChange={(id, data) =>
              orderLineUpdate.mutate({
                id,
                input: data,
              })
            }
            saveButtonBarState={orderDraftFinalize.opts.status}
            onProfileView={() => navigate(customerUrl(order.user.id))}
          />
        </OrderLineDiscountProvider>
      </OrderDiscountProvider>
      <OrderDraftCancelDialog
        confirmButtonState={orderDraftCancel.opts.status}
        errors={orderDraftCancel.opts.data?.draftOrderDelete.errors || []}
        onClose={closeModal}
        onConfirm={handleOrderDraftCancel}
        open={params.action === "cancel"}
        orderNumber={getStringOrPlaceholder(order?.number)}
      />
      <OrderDraftFinalizeDialog
        confirmButtonState={orderDraftFinalize.opts.status}
        errors={orderDraftFinalize.opts.data?.draftOrderComplete.errors || []}
        open={params.action === "finalize"}
        orderNumber={getStringOrPlaceholder(order?.number)}
        notifyCustomer={finalizeNotifyCustomer}
        onClose={closeModal}
        onConfirm={handleDraftFinalize}
        handleNotifyCustomer={setFinalizeNotifyCustomer}
      />
      <OrderMarkAsPaidDialog
        confirmButtonState={orderPaymentMarkAsPaid.opts.status}
        errors={orderPaymentMarkAsPaid.opts.data?.orderMarkAsPaid.errors || []}
        open={params.action === "mark-paid"}
        transactionReference={transactionReference}
        handleTransactionReference={({ target }) => setTransactionReference(target.value)}
        notifyCustomer={markAsPaidNotifyCustomer}
        handleNotifyCustomer={setMarkAsPaidNotifyCustomer}
        onClose={closeModal}
        onConfirm={() =>
          orderPaymentMarkAsPaid.mutate({
            id,
            transactionReference,
            notifyCustomer: markAsPaidNotifyCustomer,
          })
        }
      />
      <OrderShippingMethodEditDialog
        confirmButtonState={orderShippingMethodUpdate.opts.status}
        errors={orderShippingMethodUpdate.opts.data?.orderUpdateShipping.errors || []}
        open={params.action === "edit-shipping"}
        shippingMethod={order?.shippingMethod?.id}
        shippingMethods={order?.shippingMethods}
        onClose={closeModal}
        onSubmit={variables =>
          orderShippingMethodUpdate.mutate({
            id,
            input: {
              shippingMethod: variables.shippingMethod,
            },
          })
        }
      />
      <OrderProductAddDialog
        confirmButtonState={orderLinesAdd.opts.status}
        errors={orderLinesAdd.opts.data?.orderLinesCreate.errors || []}
        loading={variantSearchOpts.loading}
        open={params.action === "add-order-line"}
        hasMore={variantSearchOpts.data?.search?.pageInfo?.hasNextPage}
        products={mapEdgesToItems(variantSearchOpts?.data?.search)}
        onClose={closeModal}
        onFetch={variantSearch}
        onFetchMore={loadMore}
        channelName={order.channel.name}
        onSubmit={variants =>
          extractMutationErrors(
            orderLinesAdd.mutate({
              id,
              input: variants.map(variant => ({
                quantity: 1,
                variantId: variant.id,
              })),
            }),
          )
        }
      />
      <OrderCustomerChangeDialog
        open={params.action === "customer-change"}
        onClose={closeModal}
        onConfirm={handleCustomerChangeAction}
      />
      <OrderMetadataDialog
        open={params.action === "view-metadata"}
        onClose={closeModal}
        lineId={params.id}
        orderId={id}
      />
      <OrderAddressFields
        action={params?.action}
        orderShippingAddress={order?.shippingAddress}
        orderBillingAddress={order?.billingAddress}
        customerAddressesLoading={customerAddressesLoading}
        isDraft
        countries={data?.shop?.countries}
        customer={customerAddresses?.user}
        orderNif={getOrderNif(order)}
        onNifSubmit={handleNifUpdate}
        onClose={closeModal}
        onConfirm={handleCustomerChangeAddresses}
        confirmButtonState={orderDraftUpdate.opts.status}
        errors={orderDraftUpdate.opts.data?.draftOrderUpdate.errors}
      />
    </>
  );
};
